import { useEffect } from "react";
import {
  CREATE_STATUS_PHASE_CREATING,
  CREATE_STATUS_PHASE_ERROR,
  CREATE_STATUS_PHASE_SUCCESS,
} from "./actionItemCreatePhases";

/** Map a main-process capture phase onto the shared create-feedback banner. */
export const buildCaptureCreateFeedback = (status) => {
  const phase = String(status?.phase || "").trim();
  const message = String(status?.message || "").trim();
  if (phase === "creating") {
    return { phase: CREATE_STATUS_PHASE_CREATING, message: message || "Creating task…" };
  }
  if (phase === "error") {
    return {
      phase: CREATE_STATUS_PHASE_ERROR,
      message: message || "Could not create task",
    };
  }
  if (phase === "success") {
    return {
      phase: CREATE_STATUS_PHASE_SUCCESS,
      message: message ? `Task created: ${message}` : "Task created",
    };
  }
  return null;
};

/**
 * Wire main-process capture phases to a feedback callback. Returns an
 * unsubscribe function (a no-op outside Electron).
 *
 * The initial getCaptureStatus() read is what covers a capture triggered with
 * the window closed: the "creating" push already went out to nobody, so a
 * window that opens mid-capture has to ask for it.
 *
 * @example
 * const stop = subscribeToDesktopCaptureStatus((feedback) => setCreateFeedback(feedback));
 * stop();
 */
export const subscribeToDesktopCaptureStatus = (onFeedback) => {
  const bridge = typeof window === "undefined" ? null : window.starmeshDesktop;
  if (!bridge?.onCaptureStatus) return () => {};
  let cancelled = false;
  const apply = (status) => {
    if (cancelled) return;
    const feedback = buildCaptureCreateFeedback(status);
    if (feedback) onFeedback(feedback);
  };
  const pending = bridge.getCaptureStatus?.();
  if (pending?.then) {
    pending.then(apply).catch(() => {});
  }
  const stop = bridge.onCaptureStatus(apply);
  return () => {
    cancelled = true;
    stop?.();
  };
};

/**
 * Mirror desktop hotkey/tray captures into the tasks page's create-feedback
 * banner, so a capture started from another app shows the same "Creating
 * task…" spinner as a voice or typed add.
 *
 * @example
 * useDesktopCaptureFeedback(setCreateFeedback, { enabled: !isLandingDemo });
 * // Ctrl+Option+K in Slack → banner shows "Creating task…", then the title
 */
export const useDesktopCaptureFeedback = (setFeedback, { enabled = true } = {}) => {
  useEffect(() => {
    if (!enabled) return undefined;
    return subscribeToDesktopCaptureStatus(setFeedback);
  }, [enabled, setFeedback]);
};

export default useDesktopCaptureFeedback;
